import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import AxiosInstance from '@/api/axios';

interface ActiveClientState {
  id: string | null;
  name: string | null;
  profile: string | null;
  salesReport: boolean | null;
  reEngage: boolean | null;
  loading: boolean;
  agents: Record<string, boolean> | null;
  googleAnalytics: boolean | null;
}

const initialState: ActiveClientState = {
  id: null,
  name: null,
  profile: null,
  salesReport: null,
  reEngage: null,
  loading: false,
  agents: null,
  googleAnalytics: null,
};

export const fetchClientById = createAsyncThunk(
  'activeClient/fetchById',
  async (clientId: string) => {
    const response = await AxiosInstance.get(`/clients/${clientId}`);
    const data = response.data.data;
    return data;
  }
);

const activeClientSlice = createSlice({
  name: 'activeClient',
  initialState,
  reducers: {
    setActiveClient: (
      state,
      action: PayloadAction<{
        id: string;
        name: string;
        profile: string | null;
        salesReport: boolean | null;
        reEngage: boolean;
        agents?: Record<string, boolean> | null;
        googleAnalytics?: { propertyId: string | null } | null;
      }>
    ) => {
      state.id = action.payload.id;
      state.name = action.payload.name;
      state.profile = action.payload.profile;
      state.salesReport = action.payload.salesReport;
      state.reEngage = action.payload.reEngage;
      state.agents = action.payload.agents ?? null;
      state.googleAnalytics = action.payload.googleAnalytics?.propertyId
        ? true
        : false;
    },
    updateActiveClientMeta: (
      state,
      action: PayloadAction<{
        salesReport: boolean;
        reEngage: boolean;
      }>
    ) => {
      state.salesReport = action.payload.salesReport;
      state.reEngage = action.payload.reEngage;
    },
    setAgents: (
      state,
      action: PayloadAction<Record<string, boolean> | null>
    ) => {
      state.agents = action.payload;
    },
    setGoogleAnalyticBool: (state, action: PayloadAction<boolean>) => {
      state.googleAnalytics = action.payload;
    },
    clearActiveClient: (state) => {
      state.id = null;
      state.name = null;
      state.profile = null;
      state.salesReport = null;
      state.reEngage = null;
      state.agents = null;
      state.googleAnalytics = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchClientById.fulfilled, (state, action) => {
        state.loading = false;
        state.id = action.payload._id;
        state.name = action.payload.name;
        state.profile = action.payload.media?.path ?? null;
        state.salesReport = action.payload.salesReport ?? null;
        state.reEngage = action.payload.reEngage;
        state.agents = action.payload.agents ?? null;
        state.googleAnalytics = action.payload.googleAnalytics.propertyId
          ? true
          : false;
      })
      .addCase(fetchClientById.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const selectShowGoogleAnalytics = (state: {
  activeClient: ActiveClientState;
}) => !!state.activeClient.googleAnalytics;
export const {
  setActiveClient,
  updateActiveClientMeta,
  setAgents,
  setGoogleAnalyticBool,
  clearActiveClient,
} = activeClientSlice.actions;
export default activeClientSlice.reducer;
